import { useCallback, useEffect, useState } from 'react';

import { C_DEVICE_BREAKPOINTS } from '@/ts/constants/C_Overlay';
import { E_DeviceTypes } from '@/ts/enums/E_DeviceTypes';
import {
  T_UseDeviceDetectionOptions,
  T_DeviceDetectionResult
} from '@/ts/types/T_Overlay';

export function useDeviceDetection(
  options?: T_UseDeviceDetectionOptions
): T_DeviceDetectionResult {
  const breakpoints = options?.breakpoints || C_DEVICE_BREAKPOINTS;

  const calculateDeviceInfo = useCallback(
    (width: number, height: number): T_DeviceDetectionResult => {
      const deviceType =
        width <= breakpoints.mobile
          ? E_DeviceTypes.MOBILE
          : width <= breakpoints.tablet
            ? E_DeviceTypes.TABLET
            : E_DeviceTypes.DESKTOP;

      const isMobile = deviceType === E_DeviceTypes.MOBILE;
      const isTablet = deviceType === E_DeviceTypes.TABLET;
      const isDesktop = deviceType === E_DeviceTypes.DESKTOP;

      return {
        deviceType,
        isMobile,
        isTablet,
        isDesktop,
        screenWidth: width,
        screenHeight: height
      };
    },
    [breakpoints]
  );

  const [deviceInfo, setDeviceInfo] = useState<T_DeviceDetectionResult>(() => {
    if (typeof window === 'undefined') {
      return {
        deviceType: E_DeviceTypes.DESKTOP,
        isMobile: false,
        isTablet: false,
        isDesktop: true,
        screenWidth: 1024,
        screenHeight: 768
      };
    }

    const width = window.innerWidth;
    const height = window.innerHeight;

    return calculateDeviceInfo(width, height);
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Debounce resize events to prevent excessive re-renders
    let resizeTimeout: NodeJS.Timeout;

    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const width = window.innerWidth;
        const height = window.innerHeight;

        setDeviceInfo(calculateDeviceInfo(width, height));
      }, 100); // 100ms debounce
    };

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Initial check
    handleResize();

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, [calculateDeviceInfo]);

  return deviceInfo;
}
